import type { TFunction } from 'i18next';
import type { Contract, Customer, Project } from '../types';
import type { CompanyDashboardFinance } from '../services/api';
import { KPI_REGISTRY } from '../constants/kpiRegistry';
import { companyKeyFigures } from './companyKeyFigures';

/** Only the icon carries the status: done (paid out/secured), waiting, neutral. */
export type KpiTone = 'done' | 'waiting' | 'neutral';

export interface KpiTile {
  id: string;
  icon: string;
  tone: KpiTone;
  label: string;
  value: string;
  sub?: string;
  link?: { to: string; label: string };
  /** Takes the full width of the grid. */
  wide?: boolean;
  /** Shows "Skapa projekt" in the tile. */
  createProject?: boolean;
}

export interface KpiTileData {
  projects: Project[];
  customers: Customer[];
  contracts: Contract[];
  finance: CompanyDashboardFinance | null;
  /** Projects underway: the same count as "X pågående projekt" in the greeting. */
  activeProjects: number;
}

/** Choices shown as blocks further down the start page, not as tiles. */
export const BLOCK_KPI_IDS = new Set(['projectStatusChart']);
export const TILE_KPI_IDS = new Set(KPI_REGISTRY.map(kpi => kpi.id).filter(id => !BLOCK_KPI_IDS.has(id)));

/**
 * The tiles for the selected KPI ids, in the selected order. Every value is
 * one the start page already had (see companyKeyFigures for the money tiles).
 */
export function kpiTiles(ids: string[], data: KpiTileData, t: TFunction, lang: string): KpiTile[] {
  const locale = lang.startsWith('en') ? 'en-GB' : 'sv-SE';
  const kr = (amount: number | null) => amount === null
    ? '—'
    : new Intl.NumberFormat(locale, { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);
  const count = (value: number) => new Intl.NumberFormat(locale).format(value);
  const show = (to: string) => ({ to, label: t('home.keyFigures.show') });
  const figures = companyKeyFigures(data.contracts, data.finance);
  const { projects, customers } = data;

  const tile = (id: string): KpiTile | null => {
    const def = KPI_REGISTRY.find(kpi => kpi.id === id);
    if (!def || BLOCK_KPI_IDS.has(id)) return null;
    const base = { id, icon: def.icon, tone: 'neutral' as KpiTone, label: t(`home.keyFigures.labels.${id}`) };
    switch (id) {
      case 'completedVolume':
        return { ...base, icon: 'check-circle', tone: 'done', value: kr(figures.paidOutSek), sub: t('home.keyFigures.paidOutSub') };
      case 'totalPaid':
        return { ...base, icon: 'shield', tone: 'done', value: kr(figures.securedSek), sub: t('home.keyFigures.securedSub') };
      case 'outstandingBalance':
        return {
          ...base, icon: 'clock', tone: 'waiting', value: kr(figures.awaitingPaymentSek), sub: t('home.keyFigures.awaitingPaymentSub'),
          link: figures.awaitingPaymentSek > 0 ? show('/projects?pendingPayments=true') : undefined
        };
      case 'totalContractValue':
        return {
          ...base, icon: 'file-text', value: kr(figures.bidValueSek),
          sub: figures.bidCount > 0
            ? t('home.keyFigures.bidsSub', { company: figures.bidCount - figures.bidsAwaitingCustomer, customer: figures.bidsAwaitingCustomer })
            : t('home.keyFigures.bidsNone'),
          link: figures.bidCount > 0 ? show('/projects') : undefined
        };
      case 'activeVsCompleted':
        return {
          ...base, icon: 'folder', value: count(data.activeProjects), sub: t('home.keyFigures.activeProjectsSub'),
          link: data.activeProjects > 0 ? show('/projects') : undefined
        };
      case 'customersWithoutProject': {
        const withProject = new Set(projects.map(project => project.customerId).filter(Boolean));
        const value = customers.filter(customer => !withProject.has(customer.id)).length;
        return { ...base, tone: value > 0 ? 'waiting' : 'neutral', value: count(value), sub: t('home.keyFigures.sub.customersWithoutProject'), createProject: value > 0 };
      }
      case 'customers': {
        const verified = customers.filter(customer => customer.verifiedCustomer === 'Verifierad').length;
        const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
        const fresh = customers.filter(customer => customer.createdAt >= monthStart).length;
        return {
          ...base, value: count(customers.length), sub: t('home.keyFigures.sub.customers', { verified, fresh }),
          link: customers.length > 0 ? show('/customers') : undefined
        };
      }
      default:
        return null;
    }
  };

  return ids.map(tile).filter((item): item is KpiTile => item !== null);
}
